function formatMoney(amount) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount / 100);
}

function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.isCartUpdating = false;
    this.activeElement = null;
    this._boundClickHandler = this.handleCartDrawerClick.bind(this);

    this.addEventListener("keyup", (evt) => evt.code === "Escape" && this.close());

    const overlay = this.querySelector("#CartDrawer-Overlay");
    if (overlay) overlay.addEventListener("click", this.close.bind(this));

    this.setHeaderCartIconAccessibility();
    this.initializeCartDrawerEvents();
    this.initializeUpsellButtons();
    this.initializeVariantSelectors();
  }

  initializeCartDrawerEvents() {
    const drawerInner = this.querySelector(".drawer__inner");
    if (!drawerInner) return;
    drawerInner.removeEventListener("click", this._boundClickHandler);
    drawerInner.addEventListener("click", this._boundClickHandler);
  }

  initializeUpsellButtons() {
    this.querySelectorAll(".upsell-add-button").forEach((button) => {
      button.onclick = (event) => this.handleUpsellButtonClick(event);
    });

    this.querySelectorAll(".upsell-checkbox").forEach((checkbox) => {
      checkbox.onchange = (event) => this.handleUpsellChange(event);
    });
  }

  initializeVariantSelectors() {
    this.querySelectorAll(".upsell-product__variant-selector").forEach((selector) => {
      selector.onchange = (event) => this.handleVariantChange(event);
    });
  }

  handleVariantChange(event) {
    const selector = event.target;
    const selectedOption = selector.options[selector.selectedIndex];
    const productContainer = selector.closest(".upsell-product");
    if (!productContainer) return;

    const productImage = productContainer.querySelector(".upsell-product-image");
    const imageUrl = selectedOption ? selectedOption.getAttribute("data-image-url") : null;
    const defaultImage = productImage ? productImage.getAttribute("data-default-image") : null;

    if (productImage) {
      if (imageUrl && !imageUrl.includes("no-image")) productImage.src = imageUrl;
      else if (defaultImage) productImage.src = defaultImage;
    }

    const addButton = productContainer.querySelector(".upsell-add-button");
    const checkbox = productContainer.querySelector(".upsell-checkbox");

    if (addButton) addButton.setAttribute("data-product-id", selector.value);
    if (checkbox) {
      checkbox.setAttribute("data-product-id", selector.value);
      checkbox.id = `upsell-product-${selector.value}`;
    }
  }

  handleUpsellButtonClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const button = event.target.closest(".upsell-add-button") || event.target;
    const productContainer = button.closest(".upsell-product");
    const variantSelector = productContainer ? productContainer.querySelector(".upsell-product__variant-selector") : null;
    const variantId = variantSelector && variantSelector.value ? variantSelector.value : button.getAttribute("data-product-id");

    if (!variantId) {
      console.error("Upsell variant ID is undefined");
      return;
    }

    button.disabled = true;
    const oldText = button.textContent;
    button.textContent = "Agregando...";

    this.addProductToCart(variantId)
      .finally(() => {
        button.disabled = false;
        button.textContent = oldText || "Agregar";
      });
  }

  handleUpsellChange(event) {
    const checkbox = event.target;
    const productContainer = checkbox.closest(".upsell-product");
    const variantSelector = productContainer ? productContainer.querySelector(".upsell-product__variant-selector") : null;
    const variantId = variantSelector && variantSelector.value ? variantSelector.value : checkbox.getAttribute("data-product-id");

    if (!variantId) {
      console.error("Upsell variant ID is undefined");
      return;
    }

    if (checkbox.checked) this.addProductToCart(variantId);
    else this.removeProductFromCart(variantId);
  }

  handleCartDrawerClick(event) {
    if (event.target.closest(".quantity__button") || event.target.closest("cart-remove-button") || event.target.closest(".cart-remove-button")) {
      setTimeout(() => {
        this.updateCart();
      }, 700);
    }
  }

  updateCart() {
    this.isCartUpdating = true;

    return fetch(`${routes.cart_url}?section_id=cart-drawer`, { credentials: "same-origin" })
      .then((response) => response.text())
      .then((cartDrawerHtml) => {
        const cartDrawerFragment = document.createElement("div");
        cartDrawerFragment.innerHTML = cartDrawerHtml;

        this.getSectionsToRender().forEach((section) => {
          const sectionElement = document.querySelector(section.selector);
          const newSectionElement = cartDrawerFragment.querySelector(section.selector);

          if (sectionElement && newSectionElement) {
            sectionElement.innerHTML = newSectionElement.innerHTML;
          }
        });

        this.updateCartCount();
        this.initializeCartDrawerEvents();
        this.initializeUpsellButtons();
        this.initializeVariantSelectors();
        this.fixContinueShoppingLinks();
        if (window.HeroGlowInitCartCountdowns) window.HeroGlowInitCartCountdowns(this);
        if (window.HeroGlowInitCartCountdowns) window.HeroGlowInitCartCountdowns(this);
      })
      .catch((error) => {
        console.error("Error updating cart:", error);
      })
      .finally(() => {
        this.isCartUpdating = false;
      });
  }

  updateCartFooter() {
    return this.updateCart();
  }

  updateCartCount() {
    return fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        if (!cart || typeof cart.item_count !== 'number') return;
        document.querySelectorAll('.cart-count-bubble span, .cart-count-bubble, #cart-icon-bubble .cart-count-bubble span, [data-cart-count], .cart-count, .header-cart-count').forEach((el) => {
          el.textContent = cart.item_count;
          el.style.display = cart.item_count > 0 ? '' : 'none';
        });
      })
      .catch(() => {});
  }

  addProductToCart(variantId) {
    if (!variantId) return Promise.resolve();
    if (this.isCartUpdating) return Promise.resolve();

    this.isCartUpdating = true;

    return fetch("/cart/add.js", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
    })
      .then((response) => {
        if (!response.ok) throw new Error("Failed to add product to cart: " + response.statusText);
        return response.json();
      })
      .then((data) => {
        this.dispatchEvent(new CustomEvent("cartUpdated", { bubbles: true }));
        document.dispatchEvent(new CustomEvent("cart:updated", { detail: data }));
        window.dispatchEvent(new CustomEvent("cart:updated", { detail: data }));
        return this.updateCart();
      })
      .catch((error) => {
        console.error("Error adding product to cart:", error);
      })
      .finally(() => {
        this.isCartUpdating = false;
      });
  }

  removeProductFromCart(variantId) {
    if (!variantId) return Promise.resolve();
    if (this.isCartUpdating) return Promise.resolve();

    this.isCartUpdating = true;

    return fetch("/cart/change.js", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ id: String(variantId), quantity: 0 }),
    })
      .then((response) => response.json())
      .then((data) => {
        this.dispatchEvent(new CustomEvent("cartUpdated", { bubbles: true }));
        document.dispatchEvent(new CustomEvent("cart:updated", { detail: data }));
        window.dispatchEvent(new CustomEvent("cart:updated", { detail: data }));
        return this.updateCart();
      })
      .catch((error) => {
        console.error("Error removing product from cart:", error);
      })
      .finally(() => {
        this.isCartUpdating = false;
      });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;

    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.updateCart().then(() => this.open(cartLink));
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code && event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.updateCart().then(() => this.open(cartLink));
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role")) this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => this.classList.add("animate", "active"));

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.querySelector("#CartDrawer");
        const focusElement = this.querySelector(".drawer__inner") || this.querySelector(".drawer__close");
        if (containerToTrapFocusOn && focusElement && typeof trapFocus === 'function') trapFocus(containerToTrapFocusOn, focusElement);
        this.initializeUpsellButtons();
        this.initializeVariantSelectors();
        this.fixContinueShoppingLinks();
        if (window.HeroGlowInitCartCountdowns) window.HeroGlowInitCartCountdowns(this);
      },
      { once: true }
    );

    document.body.classList.add("overflow-hidden");
  }

  close() {
    this.classList.remove("active");
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling && cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute("aria-controls", cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute("aria-expanded", !event.currentTarget.closest("details").hasAttribute("open"));
    });

    if (cartDrawerNote.parentElement && typeof onKeyUpEscape === 'function') cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector(".drawer__inner");
    if (drawerInner && drawerInner.classList.contains("is-empty")) drawerInner.classList.remove("is-empty");
    this.productId = parsedState.id;

    if (parsedState && parsedState.sections) {
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
        if (!sectionElement || !parsedState.sections[section.id]) return;
        const newHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        if (newHTML) sectionElement.innerHTML = newHTML;
      });
    } else {
      this.updateCart();
    }

    setTimeout(() => {
      const overlay = this.querySelector("#CartDrawer-Overlay");
      if (overlay) overlay.addEventListener("click", this.close.bind(this));
      this.open();
    });

    this.dispatchEvent(new CustomEvent("cartUpdated", { bubbles: true }));
    this.initializeCartDrawerEvents();
    this.initializeUpsellButtons();
    this.initializeVariantSelectors();
    this.fixContinueShoppingLinks();
    if (window.HeroGlowInitCartCountdowns) window.HeroGlowInitCartCountdowns(this);
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    if (!html) return "";
    const element = new DOMParser().parseFromString(html, "text/html").querySelector(selector);
    return element ? element.innerHTML : "";
  }

  getSectionsToRender() {
    return [
      { id: "cart-drawer", selector: ".drawer__inner" },
      { id: "cart-drawer", selector: ".custom-header" },
      { id: "cart-drawer", selector: ".cart-drawer__footer" },
    ];
  }

  fixContinueShoppingLinks() {
    if (window.location.pathname.includes('/products/')) {
      sessionStorage.setItem('hg_last_product_url', window.location.pathname);
    }
    const productUrl = window.location.pathname.includes('/products/')
      ? window.location.pathname
      : sessionStorage.getItem('hg_last_product_url');
    if (!productUrl) return;
    this.querySelectorAll('a, button').forEach((el) => {
      const text = (el.textContent || '').trim().toLowerCase();
      if (text.includes('seguir comprando') || text.includes('continue shopping') || text.includes('continuar comprando')) {
        if (el.tagName.toLowerCase() === 'a') el.setAttribute('href', productUrl);
        else el.onclick = () => { window.location.href = productUrl; };
      }
    });
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get("cart-drawer")) customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: "CartDrawer", section: "cart-drawer", selector: ".drawer__inner" },
      { id: "cart-icon-bubble", section: "cart-icon-bubble", selector: ".shopify-section" },
    ];
  }
}

if (!customElements.get("cart-drawer-items")) customElements.define("cart-drawer-items", CartDrawerItems);
